import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoIcon from "@mui/icons-material/Info";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../shadcn-components/ui/popover";

// Extra, page-specific usage hints shown under the page's own description
// (the description itself comes from the page header, never duplicated here).
// Keyed by route; pages without an entry just show their description.
const PAGE_INFO_DETAILS = {
  "/documents":
    "Search, filter and open any document to view, edit, assign or process it.",
  "/unprocessed-documents":
    "Open a document to review it, link it to a client and mark it processed.",
  "/processed-documents":
    "Review finished documents and reopen any record to view or update it.",
  "/client-unknown-documents":
    "Open a document to match it to the correct client.",
  "/all-assigned-case-list":
    "View document details, status and assigned user, and view, edit or reassign documents.",
  "/assigned-case-list":
    "View document details, status and assigned user, and view, edit or reassign documents.",
  "/unassigned-case-list":
    "Assign documents to users so they can be processed.",
  "/my-assigned-case-list":
    "Work through your queue: open, edit and complete the documents assigned to you.",
  "/email-case-list": "Open an email to review, assign or process its attachments.",
  "/fax-case-list": "Open a fax to review, assign or process it.",
  "/scan-case-list": "Open a scanned file to review, assign or process it.",
  "/clients":
    "Search clients, add new ones and open a client to see their documents and requests.",
  "/document-chasing":
    "Only clients with outstanding requested documents are listed here.",
  "/requests": "Track each request and see which documents have been uploaded.",
  "/starred-documents": "Star a document from any list to keep it here for quick access.",
  "/audit-trail": "Filter by document, user or date to trace what happened and when.",
  "/users": "Add, edit or deactivate users and set their roles.",
  "/roles": "Create roles and control which features each role can access.",
  "/field-settings": "Add or edit the fields captured on documents and clients.",
  "/email-templates": "Create and edit templates used for outgoing emails.",
  "/document-request-settings":
    "Create and edit the templates used when requesting documents from clients.",
  "/contract-templates": "Create and manage templates used to generate contracts.",
  "/trash-documents": "Restore deleted documents or remove them permanently.",
  "/trash-users": "Restore deleted user accounts.",
};

const CLOSE_DELAY_MS = 150;

// Small ⓘ next to a page title. Opens on hover (and on click/tap or keyboard
// focus for touch/keyboard users). The popover is portalled and
// collision-aware, so it never shifts the layout or gets clipped at the
// screen edges.
const PageInfoTooltip = ({ title, description }) => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const details =
    PAGE_INFO_DETAILS[pathname] ||
    PAGE_INFO_DETAILS[`/${pathname.split("/")[1]}`];

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  if (!description && !details) return null;

  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About ${title}`}
          className="page-info-trigger inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-nbl-primary focus-visible:text-nbl-primary focus-visible:outline-none"
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={6}
        collisionPadding={12}
        onMouseEnter={show}
        onMouseLeave={hide}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="page-info-popover w-72 max-w-[calc(100vw-24px)] rounded-lg border-gray-200 bg-white p-3 shadow-lg"
      >
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
          <InfoIcon sx={{ fontSize: 16 }} className="text-nbl-primary" />
          About {title}
        </div>
        {description && (
          <p className="mt-1.5 text-xs leading-relaxed text-gray-600">
            {/[.!?]$/.test(description) ? description : `${description}.`}
          </p>
        )}
        {details && (
          <p className="mt-1 text-xs leading-relaxed text-gray-600">{details}</p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default PageInfoTooltip;
